import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowUpRight,
    Heart,
    Users,
    HandHeart,
    Sparkles,
    Globe2,
    GraduationCap,
    Stethoscope,
    Microscope,
    Briefcase,
    Palette,
    Network,
    ArrowRight,
    ShieldCheck,
    Feather,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import Counter from "@/components/Counter";
import { TID } from "@/constants/testIds";

const colorMap = {
    olive: { bg: "bg-caireen-olive", text: "text-caireen-olive", bgSoft: "bg-caireen-olive/10" },
    yellow: { bg: "bg-caireen-yellow", text: "text-caireen-yellow", bgSoft: "bg-caireen-yellow/15" },
    blue: { bg: "bg-caireen-blue", text: "text-caireen-blue", bgSoft: "bg-caireen-blue/10" },
    orange: { bg: "bg-caireen-orange", text: "text-caireen-orange", bgSoft: "bg-caireen-orange/10" },
    red: { bg: "bg-caireen-red", text: "text-caireen-red", bgSoft: "bg-caireen-red/10" },
};

const focusIcons = [Stethoscope, GraduationCap, Microscope, HandHeart, Briefcase, Palette, Network];

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-50px" },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function Home() {
    const { t } = useLang();

    return (
        <div data-testid="page-home" className="overflow-hidden">
            {/* HERO */}
            <section className="relative pt-8 md:pt-12 pb-20 md:pb-32 px-6 md:px-10">
                {/* animated blobs */}
                <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-24 -left-16 w-[420px] h-[420px] rounded-full bg-caireen-yellow/30 blur-3xl animate-blob" />
                    <div className="absolute top-40 -right-24 w-[480px] h-[480px] rounded-full bg-caireen-orange/25 blur-3xl animate-blob" style={{ animationDelay: "3s" }} />
                    <div className="absolute bottom-0 left-1/3 w-[360px] h-[360px] rounded-full bg-caireen-olive/25 blur-3xl animate-blob" style={{ animationDelay: "6s" }} />
                </div>

                <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                    {/* Text */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-7 space-y-7"
                    >
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur border border-caireen-ink/10 font-sans text-xs tracking-wider uppercase text-caireen-ink/70">
                            <Sparkles className="w-3.5 h-3.5 text-caireen-orange" strokeWidth={2} />
                            {t.hero.eyebrow}
                        </span>
                        <h1 className="font-serif text-balance text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                            {t.hero.title}{" "}
                            <span className="italic relative inline-block">
                                {t.hero.titleAccent}
                                <motion.span
                                    initial={{ scaleX: 0 }}
                                    animate={{ scaleX: 1 }}
                                    transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                    style={{ originX: 0 }}
                                    className="absolute -bottom-2 left-0 right-0 h-3 bg-caireen-yellow/60 -z-10 rounded-full"
                                />
                            </span>
                        </h1>
                        <p className="font-sans text-lg md:text-xl text-caireen-ink/75 leading-relaxed max-w-2xl">
                            {t.hero.sub}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 pt-2">
                            <Link to="/donate">
                                <Button
                                    data-testid={TID.heroDonate}
                                    className="rounded-full bg-caireen-orange hover:bg-caireen-red text-white font-semibold px-7 py-6 text-base shadow-[0_18px_36px_-12px_rgba(232,133,51,0.55)] transition-all"
                                >
                                    <Heart className="w-4 h-4 mr-2" fill="currentColor" />
                                    {t.hero.ctaDonate}
                                </Button>
                            </Link>
                            <Link to="/about">
                                <Button
                                    data-testid={TID.heroJoin}
                                    variant="outline"
                                    className="rounded-full border-2 border-caireen-ink/15 hover:border-caireen-olive hover:bg-caireen-olive/10 text-caireen-ink font-semibold px-7 py-6 text-base bg-white/60 backdrop-blur"
                                >
                                    <Users className="w-4 h-4 mr-2" strokeWidth={2} />
                                    {t.hero.ctaJoin}
                                </Button>
                            </Link>
                        </div>

                        <div className="flex items-center gap-6 pt-3">
                            <div>
                                <Counter to={7} className="font-serif text-4xl text-caireen-orange" />
                                <p className="font-sans text-xs uppercase tracking-widest text-caireen-ink/60 mt-1">{t.hero.stat1}</p>
                            </div>
                            <span className="w-px h-10 bg-caireen-ink/15" />
                            <div>
                                <Counter to={8} className="font-serif text-4xl text-caireen-olive" />
                                <p className="font-sans text-xs uppercase tracking-widest text-caireen-ink/60 mt-1">{t.hero.stat2}</p>
                            </div>
                            <span className="w-px h-10 bg-caireen-ink/15" />
                            <div>
                                <Counter to={4} className="font-serif text-4xl text-caireen-blue" />
                                <p className="font-sans text-xs uppercase tracking-widest text-caireen-ink/60 mt-1">{t.hero.stat3}</p>
                            </div>
                        </div>

                        <div className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full bg-caireen-ink/5 font-sans text-xs text-caireen-ink/70">
                            <Globe2 className="w-3.5 h-3.5" />
                            {t.hero.badge}
                        </div>
                    </motion.div>

                    {/* Bento grid */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-5 relative"
                    >
                        <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[520px] md:h-[600px]">
                            <motion.div
                                whileHover={{ y: -4 }}
                                className="col-span-4 row-span-4 rounded-3xl overflow-hidden ring-1 ring-caireen-ink/10 shadow-[0_24px_60px_-24px_rgba(31,41,55,0.25)]"
                            >
                                <img
                                    src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?auto=format&fit=crop&w=1000&q=80"
                                    alt="Community"
                                    className="w-full h-full object-cover"
                                />
                            </motion.div>
                            <motion.div
                                whileHover={{ y: -4 }}
                                className="col-span-2 row-span-3 rounded-3xl overflow-hidden ring-1 ring-caireen-ink/10 bg-caireen-yellow/30 flex flex-col justify-end p-4"
                            >
                                <p className="font-serif text-2xl leading-tight text-caireen-ink">
                                    + 24
                                </p>
                                <p className="font-sans text-[11px] uppercase tracking-widest text-caireen-ink/70 mt-1">
                                    partners
                                </p>
                            </motion.div>
                            <motion.div
                                whileHover={{ y: -4 }}
                                className="col-span-2 row-span-3 rounded-3xl overflow-hidden ring-1 ring-caireen-ink/10"
                            >
                                <img
                                    src="https://images.pexels.com/photos/6925325/pexels-photo-6925325.jpeg?auto=compress&cs=tinysrgb&w=800"
                                    alt="Art"
                                    className="w-full h-full object-cover"
                                />
                            </motion.div>
                            <motion.div
                                whileHover={{ y: -4 }}
                                className="col-span-3 row-span-2 rounded-3xl overflow-hidden ring-1 ring-caireen-ink/10"
                            >
                                <img
                                    src="https://images.unsplash.com/photo-1529390079861-591de354faf5?auto=format&fit=crop&w=900&q=80"
                                    alt="Education"
                                    className="w-full h-full object-cover"
                                />
                            </motion.div>
                            <motion.div
                                whileHover={{ y: -4 }}
                                className="col-span-3 row-span-2 rounded-3xl bg-caireen-orange text-white p-5 flex flex-col justify-between ring-1 ring-caireen-orange/40"
                            >
                                <Heart className="w-6 h-6" fill="currentColor" />
                                <div>
                                    <p className="font-serif text-2xl leading-tight">{t.nav.donateCta}</p>
                                    <Link to="/donate" className="font-sans text-xs uppercase tracking-widest inline-flex items-center gap-1 mt-2 opacity-90 hover:opacity-100">
                                        Go <ArrowUpRight className="w-3 h-3" />
                                    </Link>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* MISSION */}
            <section className="relative py-20 md:py-28 px-6 md:px-10 bg-white" data-testid="section-mission">
                <div className="max-w-5xl mx-auto text-center">
                    <motion.p {...fadeUp} className="font-sans text-xs uppercase tracking-[0.25em] text-caireen-orange mb-5">
                        {t.mission.eyebrow}
                    </motion.p>
                    <motion.h2 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-balance">
                        {t.mission.title}
                    </motion.h2>
                    <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="mt-8 font-sans text-lg text-caireen-ink/75 leading-relaxed max-w-3xl mx-auto">
                        {t.mission.body}
                    </motion.p>
                </div>
            </section>

            {/* NAME MEANING */}
            <section className="relative py-24 md:py-32 px-6 md:px-10 overflow-hidden" data-testid="section-name-meaning">
                <div aria-hidden className="absolute -top-20 -left-20 w-[360px] h-[360px] rounded-full bg-caireen-olive/15 blur-3xl animate-blob" />
                <div aria-hidden className="absolute bottom-0 -right-20 w-[420px] h-[420px] rounded-full bg-caireen-yellow/20 blur-3xl animate-blob" style={{ animationDelay: "4s" }} />

                <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <motion.div {...fadeUp} className="lg:col-span-5 lg:sticky lg:top-28">
                        <p className="font-sans text-xs uppercase tracking-[0.25em] text-caireen-olive">
                            {t.nameMeaning.eyebrow}
                        </p>
                        <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            {t.nameMeaning.title.split(" Caireen ").length > 1 ? (
                                <>
                                    {t.nameMeaning.title.split(" Caireen ")[0]}{" "}
                                    <span className="italic relative inline-block">
                                        Caireen
                                        <motion.span
                                            initial={{ scaleX: 0 }}
                                            whileInView={{ scaleX: 1 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                            style={{ originX: 0 }}
                                            className="absolute -bottom-1 left-0 right-0 h-2.5 bg-caireen-yellow/70 -z-10 rounded-full"
                                        />
                                    </span>{" "}
                                    {t.nameMeaning.title.split(" Caireen ")[1]}
                                </>
                            ) : (
                                t.nameMeaning.title
                            )}
                        </h2>
                        <div className="mt-8 flex flex-wrap gap-2">
                            {t.nameMeaning.badges.map((b, i) => {
                                const Icon = [ShieldCheck, Heart, HandHeart, Feather][i % 4];
                                const colors = ["text-caireen-olive bg-caireen-olive/10", "text-caireen-red bg-caireen-red/10", "text-caireen-orange bg-caireen-orange/10", "text-caireen-blue bg-caireen-blue/10"];
                                return (
                                    <span key={b} className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-sans text-xs font-semibold tracking-wide ${colors[i % 4]}`}>
                                        <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                                        {b}
                                    </span>
                                );
                            })}
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-7 relative"
                    >
                        <span aria-hidden className="absolute -top-10 -left-2 font-serif text-[14rem] leading-none text-caireen-olive/15 select-none pointer-events-none">"</span>
                        <div className="relative space-y-6 font-sans text-lg leading-[1.7] text-caireen-ink/80">
                            <p className="first-letter:font-serif first-letter:text-6xl first-letter:font-semibold first-letter:text-caireen-orange first-letter:float-left first-letter:mr-2.5 first-letter:leading-[0.9] first-letter:mt-1">
                                {t.nameMeaning.p1}
                            </p>
                            <p>{t.nameMeaning.p2}</p>
                            <p className="font-serif italic text-2xl md:text-3xl leading-snug text-caireen-ink border-l-4 border-caireen-orange pl-6 py-1">
                                {t.nameMeaning.p3}
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* FOCUS AREAS */}
            <section className="relative py-24 px-6 md:px-10" data-testid="section-focus">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div>
                            <motion.h2 {...fadeUp} className="font-serif text-4xl md:text-5xl tracking-tight">
                                {t.focusTitle}
                            </motion.h2>
                            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-3 font-sans text-base md:text-lg text-caireen-ink/65 max-w-2xl">
                                {t.focusSub}
                            </motion.p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {t.focus.map((f, i) => {
                            const Icon = focusIcons[i % focusIcons.length];
                            const c = colorMap[f.c] || colorMap.orange;
                            return (
                                <motion.div
                                    key={i}
                                    data-testid={TID.focusCard(i)}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                                    whileHover={{ y: -6 }}
                                    className="group relative bg-white rounded-3xl p-7 ring-1 ring-caireen-ink/10 hover:ring-caireen-ink/20 hover:shadow-[0_24px_60px_-24px_rgba(31,41,55,0.18)] transition-all"
                                >
                                    <span className={`absolute top-0 left-7 right-7 h-1 rounded-b-full ${c.bg}`} />
                                    <div className={`w-12 h-12 rounded-2xl ${c.bgSoft} flex items-center justify-center mb-5`}>
                                        <Icon className={`w-6 h-6 ${c.text}`} strokeWidth={1.75} />
                                    </div>
                                    <h3 className="font-serif text-2xl tracking-tight mb-2">{f.t}</h3>
                                    <p className="font-sans text-sm text-caireen-ink/70 leading-relaxed">{f.d}</p>
                                </motion.div>
                            );
                        })}
                        {/* CTA card */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.55, delay: 0.4 }}
                            className="rounded-3xl p-7 bg-caireen-ink text-caireen-cream flex flex-col justify-between min-h-[260px]"
                        >
                            <Sparkles className="w-7 h-7 text-caireen-yellow" />
                            <div>
                                <p className="font-serif text-2xl leading-tight">{t.opportunities.title}</p>
                                <Link to="/projects" className="mt-4 inline-flex items-center gap-2 font-sans text-sm font-semibold text-caireen-yellow hover:text-white transition-colors">
                                    {t.opportunities.cta} <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="relative py-20 md:py-28 px-6 md:px-10 bg-white" data-testid="section-stats">
                <div className="max-w-7xl mx-auto">
                    <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-5xl text-center tracking-tight max-w-3xl mx-auto text-balance">
                        {t.statsTitle}
                    </motion.h2>
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
                        {t.stats.map((s, i) => {
                            const colors = ["text-caireen-orange", "text-caireen-olive", "text-caireen-blue", "text-caireen-red"];
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    className="text-center"
                                >
                                    <Counter to={s.v} suffix={s.v >= 100 ? "+" : ""} className={`font-serif text-6xl md:text-7xl ${colors[i]}`} />
                                    <p className="font-sans text-xs md:text-sm uppercase tracking-widest text-caireen-ink/60 mt-3">{s.l}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* OPPORTUNITIES */}
            <section className="relative py-24 px-6 md:px-10" data-testid="section-opportunities">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="relative aspect-[4/5] rounded-[2rem] overflow-hidden ring-1 ring-caireen-ink/10"
                    >
                        <img
                            src="https://images.pexels.com/photos/14965362/pexels-photo-14965362.jpeg?auto=compress&cs=tinysrgb&w=900"
                            alt="Inclusion"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-caireen-ink/45 via-transparent to-transparent" />
                        <div className="absolute bottom-6 left-6 right-6">
                            <p className="font-serif italic text-2xl text-white leading-tight">
                                {'"Every person deserves a place – and a voice."'}
                            </p>
                        </div>
                    </motion.div>

                    <div>
                        <motion.h2 {...fadeUp} className="font-serif text-4xl md:text-5xl tracking-tight">
                            {t.opportunities.title}
                        </motion.h2>
                        <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-4 font-sans text-base md:text-lg text-caireen-ink/70">
                            {t.opportunities.sub}
                        </motion.p>
                        <ul className="mt-8 space-y-3">
                            {t.opportunities.items.map((it, i) => (
                                <motion.li
                                    key={i}
                                    initial={{ opacity: 0, x: 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.05 }}
                                    className="flex items-start gap-3 font-sans"
                                >
                                    <span className="mt-1.5 inline-block w-2.5 h-2.5 rounded-full bg-caireen-orange shrink-0" />
                                    <span className="text-caireen-ink/85">{it}</span>
                                </motion.li>
                            ))}
                        </ul>
                        <div className="mt-8">
                            <Link to="/projects">
                                <Button className="rounded-full bg-caireen-ink hover:bg-caireen-ink/90 text-caireen-cream font-semibold px-7 py-6">
                                    {t.opportunities.cta}
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CALL TO ACTION */}
            <section className="relative py-24 px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-6xl mx-auto rounded-[2.5rem] bg-gradient-to-br from-caireen-orange via-caireen-red to-caireen-orange p-10 md:p-16 text-white relative overflow-hidden"
                >
                    <div aria-hidden className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/15 blur-3xl" />
                    <div aria-hidden className="absolute bottom-0 -left-10 w-72 h-72 rounded-full bg-caireen-yellow/30 blur-3xl" />
                    <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                        <div className="md:col-span-8">
                            <p className="font-sans text-xs uppercase tracking-[0.25em] opacity-80">{t.hero.eyebrow}</p>
                            <h2 className="mt-3 font-serif text-4xl md:text-5xl leading-tight tracking-tight">
                                {t.donatePage.title}
                            </h2>
                            <p className="mt-4 font-sans text-base md:text-lg opacity-90 max-w-xl">
                                {t.donatePage.sub}
                            </p>
                        </div>
                        <div className="md:col-span-4 flex flex-col gap-3">
                            <Link to="/donate">
                                <Button className="w-full rounded-full bg-white text-caireen-red hover:bg-caireen-cream font-bold px-7 py-7 text-base">
                                    <Heart className="w-5 h-5 mr-2" fill="currentColor" />
                                    {t.nav.donateCta}
                                </Button>
                            </Link>
                            <Link to="/about">
                                <Button variant="outline" className="w-full rounded-full bg-transparent border-2 border-white/60 hover:bg-white/10 text-white font-semibold px-7 py-7 text-base">
                                    {t.nav.join}
                                </Button>
                            </Link>
                        </div>
                    </div>
                </motion.div>
            </section>
        </div>
    );
}
